<template>
  <div class="substep-download-and-track-time">
    <div class="substep-header">
      <h3 data-testid="step-title">Install the desktop app and track time to a project</h3>
      <p>Sign in to the desktop app and start the timer to confirm your setup</p>
    </div>

    <div class="substep-body">
      <AppAlert
        v-if="!timeTracked"
        type="info"
        icon-classes="hi hi-info"
        message="We'll let you know the moment we can see you tracking time."
      />

      <div class="setup-story-card">
        <div class="setup-steps">
          <div
            class="setup-step"
            :class="{ completed: desktopAppInstalled }"
            data-testid="substep-download"
          >
            <span class="setup-step-icon">
              <i :class="desktopAppInstalled ? 'hi hi-check' : 'hi hi-export'" />
            </span>
            <span
              class="setup-step-label"
              data-testid="download-step-label"
            >{{ desktopAppInstalled ? 'Desktop app connected' : 'Download the app and sign in' }}</span>
            <span
              v-if="!desktopAppInstalled"
              class="setup-step-actions"
            >
              <a
                class="btn btn-primary btn-sm"
                :href="downloadUrl"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="download-button"
                @click="trackDownload"
              >
                Download
              </a>
            </span>
          </div>

          <div
            class="setup-step"
            :class="{ completed: timeTracked, pending: !desktopAppInstalled }"
            data-testid="substep-track"
          >
            <span class="setup-step-icon">
              <i :class="timeTracked ? 'hi hi-check' : 'hi hi-play'" />
            </span>
            <span class="setup-step-label">Press play to start the timer</span>
            <span
              v-if="startTimerUrl && desktopAppInstalled && !timeTracked"
              class="setup-step-actions"
            >
              <a
                class="btn btn-primary btn-sm"
                :href="startTimerUrl"
                data-testid="start-timer-button"
                @click="trackStartTimer"
              >
                Start timer
              </a>
            </span>
          </div>
        </div>

        <div
          class="signal-status"
          :class="{ tracked: timeTracked }"
          data-testid="signal-status"
        >
          <template v-if="timeTracked">
            <i class="hi hi-check-circle" />
            <span>Got it — we can see you're tracking time.</span>
          </template>
          <template v-else>
            <span class="signal-dot" />
            <span>{{ desktopAppInstalled ? 'Waiting for the timer to start…' : 'Waiting for you to sign in to the desktop app…' }}</span>
          </template>
        </div>
      </div>

      <button
        type="button"
        class="need-help-trigger"
        data-testid="need-help-trigger"
        @click="showHelpModal = true"
      >
        Need help, can't track, or doesn't apply?
      </button>
    </div>

    <TrackTimeNeedHelpModal
      :show="showHelpModal"
      :quick-start-guide-url="quickStartGuideUrl"
      @close="showHelpModal = false"
      @track-link="trackHelpLink"
      @continue-without-tracking="$emit('skipSubstep')"
    />
  </div>
</template>

<script>
import TrackTimeImmediatelyChannel from '~/channels/track_time_immediately_channel';
import TrackTimeNeedHelpModal from '~/components/growth_wizard/getting_started/substeps/components/TrackTimeNeedHelpModal.vue';

const POLL_INTERVAL_MS = 10000;

export default {
  name: 'DownloadAndTrackTime',
  components: { TrackTimeNeedHelpModal },
  props: {
    downloadUrl: {
      type: String,
      required: true,
    },
    desktopAppInstalled: {
      type: Boolean,
      default: false,
    },
    startTimerUrl: {
      type: String,
      default: null,
    },
    timeTracked: {
      type: Boolean,
      default: false,
    },
    hasProject: {
      type: Boolean,
      default: false,
    },
    quickStartGuideUrl: {
      type: String,
      required: true,
    },
    reloadSubstep: {
      type: Function,
      required: true,
    },
  },
  emits: ['completeSubstep', 'skipSubstep', 'trackWizardEvent'],
  data() {
    return {
      showHelpModal: false,
      pollTimer: null,
      subscription: null,
    };
  },
  watch: {
    timeTracked(detected) {
      if (detected) {
        this.stopListening();
        this.$emit('completeSubstep');
      }
    },
  },
  mounted() {
    if (this.timeTracked || !this.hasProject) {
      this.$emit('completeSubstep');

      return;
    }

    this.startListening();
  },
  beforeUnmount() {
    this.stopListening();
  },
  methods: {
    startListening() {
      this.subscription = TrackTimeImmediatelyChannel.subscribe(() => this.reloadSubstep());
      this.pollTimer = setInterval(() => this.reloadSubstep(), POLL_INTERVAL_MS);
    },
    stopListening() {
      this.subscription?.unsubscribe();
      this.subscription = null;
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    },
    trackStartTimer() {
      this.$emit('trackWizardEvent', 'Desktop app start timer clicked', {
        experiment: true,
        source: 'user_getting_started_track_time',
      });
    },
    trackHelpLink(link) {
      this.$emit('trackWizardEvent', 'User onboarding help link clicked', {
        type: 'user_getting_started',
        link,
      });
    },
    trackDownload() {
      this.$emit('trackWizardEvent', 'Desktop app download clicked', {
        experiment: true,
        source: 'user_getting_started_track_time',
      });
    },
  },
};
</script>
